import EventHandlersRegister from '@/events/events.handlers.register';
import DetectorService from '@/services/detector.service';
import errorHandlerService from '@/services/error/error.handler.service';
import { faviconService } from '@/services/favicon.service';
import siteConfigService from '@/services/site.config.service';
import { ssoService } from '@/services/sso.service';
import storageService from '@/services/storage.service';
import store from '@/store';
import { thirdPartyJavascriptSupport } from '@/third-party-javascript-support';
import { clearLocalStorageRedirectToLogoutService } from '@/services/clearLocalStorage.redirectto.logout.service';
import funcUtils from '@/utils/funcs-cross-browser';
import Logger from '@/utils/logger.utils';
import StringUtils from '@/utils/string-utils';
import WindowUtils from '@/utils/window-utils';
import {
  envService,
  pageAttributesService,
  pageService,
  siteConfigService as coreSiteConfigService,
  StringUtils as coreStringUtils,
  TrackingConfig,
  URLUtils,
  VersionedWidgetInfo,
  WindowUtils as WindowUtilsService,
  EnvInfo,
  WIDGET,
  PageAttributes,
  sessionService,
  userService,
  commonService,
} from '@ol/ol-ui-core';
import 'core-js/full/string/includes';
import Maska from 'maska';
import VeeValidate from 'vee-validate';
import Vue from 'vue';
import App from './App.vue';
import './assets/css/global.scss';
import router from './router';
import { accessibilityService } from './services/accessibility.service';
import BrowserService from './services/browser.service';
import { configService } from './services/config.service';
import oidcCallbackProcessor from './services/oidc/oidcCallbackProcessor';
import Cookie from './utils/cookie-utils';
import { LOGOUT_TYPE } from './entities/site.configuration';

const WCWrapper = () => import(/* webpackChunkName: "WCWrapper" */ '@/components/web-component-integration/wrapper.vue');

Logger.info(`process.env.NODE_ENV=${process.env.NODE_ENV}`);

funcUtils.init();

(async () => {
  let redirectedToHome: boolean = false;

  let buyerView:boolean = false;
  const environmentInfo: EnvInfo = await configService.getEnvInfo();
  envService.setEnv(environmentInfo.env);
  const clearStorageAndLogoutType: LOGOUT_TYPE = clearLocalStorageRedirectToLogoutService.getLogOutType();
  if (clearStorageAndLogoutType === LOGOUT_TYPE.CLASSIC) {
    await clearLocalStorageRedirectToLogoutService.clearLocalStorageAndClassicLogout();
    return;
  } else if (clearStorageAndLogoutType === LOGOUT_TYPE.OKTA) {
    await clearLocalStorageRedirectToLogoutService.clearLocalStorageAndOKTALogout();
    return;
  }

  if (process.env.NODE_ENV === 'development' || process.env.NODE_ENV === 'test' || process.env.NODE_ENV === 'e2etest') {
    document.cookie = 'env=development';
  }
  if (process.env.NODE_ENV !== 'development' && process.env.NODE_ENV !== 'test' && process.env.NODE_ENV !== 'e2etest') {
    const hasSiteInfoInStorage: boolean = await storageService.hasSiteInfoInStorage();
    if (!hasSiteInfoInStorage) {
      WindowUtils.setLocationHref('/stdpl/logout.html');
      redirectedToHome = true;
    }
  }
  await siteConfigService.initSiteInfo();
  const plId: number = await storageService.getPrivateLabelId();
  let orgId: number = await userService.getCurrentOrgId(plId);
  if (!orgId || orgId <= 0) {
    orgId = WindowUtilsService.getRequestInfo()?.paramInfo?.paramValue?.currentOrgId ? Number(WindowUtilsService.getRequestInfo()?.paramInfo?.paramValue?.currentOrgId) : null;
    if (orgId) {
      commonService.getOrGenerateCommonProperties(plId, orgId, null, null, null);
      /* TODO: for ford the {orgValueSelected} should be orgId-plId - need to handle separately */
      const selectUrl = (await siteConfigService.getApiEndpoint('organization.selectorUrl')).replace('{orgValueSelected}', `${orgId}-${plId}`);
      WindowUtils.setLocationHref(StringUtils.setParamValue(selectUrl, 'redirectUrl', URLUtils.encode(window.location.href)));
    }
  }
  await ssoService.loginContextScriptInit();
  buyerView = await ssoService.ssoTokenValidation();
  if (!buyerView) {
    const shouldStop = await oidcCallbackProcessor.checkAndHandleOidcLoginCallbackFlow(plId, false);
    if (shouldStop) return;
  }

  const wcWrapperWidgetInfo: VersionedWidgetInfo = await coreSiteConfigService.getVersionedWidgetInfo(plId, WIDGET.GENERIC_WIDGET_WRAPPER);
  if (wcWrapperWidgetInfo) {
    // @ts-ignore
    document.dispatchEvent(new CustomEvent('kar.ui.wc.wrapper.info', { detail: { tag: wcWrapperWidgetInfo.tag, url: wcWrapperWidgetInfo.url } }));
  }
  if (!redirectedToHome) {
    // fix can not input error on andriod phone
    let veeConfig = {
      events: 'input|blur',
    };
    const ua = navigator.userAgent.toLowerCase();
    const isAndroid = ua.indexOf('android') > -1; // && ua.indexOf("mobile");
    if (isAndroid) {
      veeConfig = {
        events: 'change|blur',
      };
    }
    Vue.use(Maska);
    Vue.use(VeeValidate, veeConfig);
    Vue.directive('$model', {
      bind(el: any, binding: any, vnode: any) {
        el.oninput = () => {
          vnode.context[binding.expression] = el.value;
        };
      },
    });
    Vue.directive('prop', {
      bind(el: any, binding: any, vnode: any) {
        const propName: string = coreStringUtils.dashCaseToCamelCase(binding.arg);
        const value: any = binding.value;
        el[propName] = value;
      },
    });


    const myMixin = {
      async created():Promise<void> {
        const pageAttributes: PageAttributes = await pageAttributesService.getPageAttributes(plId);
        const attributes = pageAttributes.attributes;
        const siteMiscConfigs = attributes.find(attribute => attribute.attributes?.siteMiscConfigs);
        const seo = JSON.parse(siteMiscConfigs.attributes.siteMiscConfigs)?.seo;

        if (seo) {
          if (seo.hideSiteOnSearchEngine) {
            const googleRobotsMeta = document.createElement('meta');
            googleRobotsMeta.setAttribute('content', 'noindex, nofollow');
            googleRobotsMeta.setAttribute('name', 'robots');
            document.head.appendChild(googleRobotsMeta);
          }
        }
      },
    };
    /* Vue.component('wc', WCWrapper); */
    Vue.config.ignoredElements = ['ol-wc'];
    const vue = new Vue({
      router,
      store,
      mixins: [myMixin],
      render: h => h(App),
      async beforeCreate() {
        EventHandlersRegister.register();
        await errorHandlerService.setupInterceptors();
        await configService.initLoadConfigsIfNeeded();
        await sessionService.initUserSessionTracking();
      },
      async created() {
        const envInfo: EnvInfo = await configService.getEnvInfo();
        envService.setEnv(envInfo.env);
        await siteConfigService.initSiteInfo();
        await store.dispatch('mainModule/resetAll');
        await DetectorService.monitor();

        try {
          const trackingConfig: TrackingConfig = await pageService.getTrackingConfig(plId);
          if (trackingConfig) {
            document.dispatchEvent(new CustomEvent('kar.ui.tracking.event.config', { detail: trackingConfig }));
          }
          await thirdPartyJavascriptSupport.init(plId);
        } catch (e) {
          Logger.error(e);
        }
        try {
          const versionedWidgetInfo: VersionedWidgetInfo = await coreSiteConfigService.getVersionedWidgetInfo(plId, 'genericWidgetWrapper', null);
          if (versionedWidgetInfo) {
            document.dispatchEvent(new CustomEvent('kar.ui.wc.wrapper.info', { detail: versionedWidgetInfo }));
          }
        } catch (e) {
          Logger.error(e);
        }
      },

      async mounted() {
        try {
          await this.updateFavicon();
          await accessibilityService.showAccessibityWidget();

          // @ts-ignore
          storageService.savePageTitle(this.$router.history.pending.meta.title.default);
          // @ts-ignore
          storageService.saveFromPage(this.$router.history.pending.name);
        } catch (e) {
          Logger.error(e);
        }
        try {
          const privateLabelId: number = await storageService.getPrivateLabelId();
          await pageAttributesService.setCssCustomProperties(privateLabelId);
        } catch (e) {
          Logger.error(e);
        }
      },
      beforeDestroy: () => {
        EventHandlersRegister.unRegister();
        DetectorService.stopMonitor();
      },
      methods: {
        async updateFavicon(): Promise<void> {
          const fUrl = await faviconService.getFaviconIcon();
          if (fUrl != null) {
            const array: any = document.querySelectorAll("link[rel*='icon']");
            for (let index = 0; index < array.length; index++) {
              array[index].href = fUrl;
            }
          }
        },
      },
    });
    vue.$mount('#app');
  }

  // append appshell related css and javascript in main file only.
  const appShellJavaScript = document.createElement('script');
  appShellJavaScript.type = 'text/javascript';
  appShellJavaScript.src = '/oliq/res/app_shell_container_check.js';
  document.head.appendChild(appShellJavaScript);
  const isInNativeAppShell = Cookie.getCookie('isInNativeAppShellContainerString') === 'true';
  if (isInNativeAppShell) {
    const appShellJavaLink = document.createElement('link');
    appShellJavaLink.type = 'text/css';
    appShellJavaLink.rel = 'stylesheet';
    const isInIosNativeAppShellContainer = Cookie.getCookie('isInIosNativeAppShellContainerString') === 'true';
    if (isInIosNativeAppShellContainer) {
      appShellJavaLink.href = '/oliq/res/app_shell_container_ios.css';
      Logger.info('iOS related App Shell styling is appended to head tag');
    } else {
      appShellJavaLink.href = '/oliq/res/app_shell_container.css';
      Logger.info('Android related App Shell styling is appended to head tag');
    }
    document.head.appendChild(appShellJavaLink);
  }

  // adding script to support variable css in internet explorer.
  if (BrowserService.unsupportedIEVersion) {
    if (!document.getElementById('ieSpecificVarCssSupportScript')) {
      const ieVariableCssScript = document.createElement('script');
      ieVariableCssScript.id = 'ieSpecificVarCssSupportScript';
      ieVariableCssScript.type = 'text/javascript';
      ieVariableCssScript.src = '/oliq/res/ie_variable_css_support.txt';
      document.head.appendChild(ieVariableCssScript);
    }
  }
})();
